import { useState, type KeyboardEvent } from 'react'
import { Check, History, Search } from 'lucide-react'
import {
  useCommissionReview,
  useCommissionReviewerUsers,
} from '../../shared/api/generated/commercial-commissions/commercial-commissions'
import type { CommissionReviewItem } from '../../shared/api/generated/model'
import { Drawer } from '../../shared/ui/Drawer'
import { AttributionForm } from './AttributionForm'
import { errorMessage, issueLabels, plural, shortDate } from './helpers'

const PAGE = 50

/** Metadata-only review: no commission lines, rates or other sellers' totals. */
export function AttributionReview({ competence }: { competence: string }) {
  const [state, setState] = useState<'pending' | 'all'>('pending')
  const [search, setSearch] = useState('')
  const [allMonths, setAllMonths] = useState(false)
  const [offset, setOffset] = useState(0)
  const [editing, setEditing] = useState<CommissionReviewItem | null>(null)
  const users = useCommissionReviewerUsers({ query: { enabled: !!editing } })
  const userName = (id: unknown) =>
    id == null
      ? 'não informado'
      : (users.data?.find((u) => u.user_id === id)?.name ?? `usuário #${String(id)}`)
  const review = useCommissionReview(
    {
      state,
      search,
      competence: allMonths ? undefined : competence,
      offset,
      limit: PAGE,
    },
    { query: { retry: false, refetchInterval: 60000 } },
  )
  const total = review.data?.total ?? 0
  const itemKey = (a: CommissionReviewItem) =>
    a.proposal_id ? `p-${a.proposal_id}` : `s-${a.legacy_surgery_id}`
  const editingKey = editing ? itemKey(editing) : null
  const onRowKey = (a: CommissionReviewItem) => (e: KeyboardEvent<HTMLTableRowElement>) => {
    if (e.target !== e.currentTarget) return
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      setEditing(a)
    }
  }
  return (
    <section className="cc-card">
      <div className="cc-card-head">
        <div>
          <h2>Conferência de atribuições</h2>
          <p>
            Originador e fechador de cada deal. Corrigir não exige aprovação nem gera pagamento.
          </p>
        </div>
        {review.data && <span className="cc-count">{plural(total, 'deal', 'deals')}</span>}
      </div>

      <div className="cc-card-toolbar" role="group" aria-label="Filtros da conferência">
        <label className={`cc-chip cc-chip-select${state === 'all' ? ' is-set' : ''}`}>
          <span className="cc-chip-key">Exibir</span>
          <select
            aria-label="Exibir"
            value={state}
            onChange={(e) => {
              setState(e.target.value as 'pending' | 'all')
              setOffset(0)
            }}
          >
            <option value="pending">Com pendências</option>
            <option value="all">Todos</option>
          </select>
        </label>
        <label className="cc-chip cc-chip-search">
          <Search size={13} aria-hidden="true" />
          <input
            type="search"
            aria-label="Buscar paciente, deal ou responsável"
            placeholder="Paciente, deal ou responsável"
            value={search}
            maxLength={200}
            onChange={(e) => {
              setSearch(e.target.value)
              setOffset(0)
            }}
          />
        </label>
        <label className={`cc-chip cc-chip-toggle${allMonths ? ' is-set' : ''}`}>
          <input
            type="checkbox"
            checked={allMonths}
            onChange={(e) => {
              setAllMonths(e.target.checked)
              setOffset(0)
            }}
          />
          {allMonths ? (
            <Check size={13} aria-hidden="true" />
          ) : (
            <History size={13} aria-hidden="true" />
          )}
          Todo o histórico
        </label>
      </div>

      {review.isLoading && (
        <div className="cc-empty" role="status">
          Carregando atribuições…
        </div>
      )}
      {review.isError && (
        <p className="cc-notice cc-error cc-notice-inset" role="alert">
          {errorMessage(review.error)}
        </p>
      )}
      {review.data &&
        (review.data.items.length ? (
          <>
            <div className="cc-table-wrap">
              <table className="cc-table cc-table-clickable">
                <thead>
                  <tr>
                    <th>Paciente</th>
                    <th>Originador</th>
                    <th>Fechador</th>
                    <th>Conferência</th>
                  </tr>
                </thead>
                <tbody>
                  {review.data.items.map((a) => {
                    const issues = a.issues.map((issue) => issueLabels[issue] ?? issue)
                    const key = itemKey(a)
                    return (
                      <tr
                        key={key}
                        className={editingKey === key ? 'is-active' : undefined}
                        tabIndex={0}
                        aria-label={`Revisar ${a.patient_name}`}
                        onClick={() => setEditing(a)}
                        onKeyDown={onRowKey(a)}
                      >
                        <td className="cc-cell-patient">
                          <span className="cc-primary">{a.patient_name}</span>
                          <span className="cc-ref">
                            {a.proposal_id
                              ? `Deal #${a.proposal_id}`
                              : `Cirurgia #${a.legacy_surgery_id}`}
                          </span>
                        </td>
                        <td>
                          {a.originator_name ?? (
                            <span className="cc-undefined">Não identificado</span>
                          )}
                        </td>
                        <td>
                          {a.closer_name ?? <span className="cc-undefined">Não identificado</span>}
                        </td>
                        <td>
                          {issues.length ? (
                            <span className="cc-status is-warn" title={issues.join(' · ')}>
                              {issues[0]}
                              {issues.length > 1 && (
                                <span className="cc-dim"> +{issues.length - 1}</span>
                              )}
                            </span>
                          ) : (
                            <span className="cc-status is-neutral">Preenchida</span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <div className="cc-card-foot">
              <span>
                {offset + 1}–{Math.min(offset + PAGE, total)} de {total}
              </span>
              {total > PAGE && (
                <div>
                  <button
                    type="button"
                    className="btn btn-sm"
                    disabled={!offset}
                    onClick={() => setOffset(offset - PAGE)}
                  >
                    Anterior
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm"
                    disabled={offset + PAGE >= total}
                    onClick={() => setOffset(offset + PAGE)}
                  >
                    Próxima
                  </button>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="cc-empty">
            <p>Nenhuma atribuição encontrada.</p>
            <span>Amplie com “Exibir: Todos” ou “Todo o histórico”.</span>
          </div>
        ))}

      {editing && (
        <Drawer
          open
          width={520}
          onClose={() => setEditing(null)}
          title={
            <div className="drawer-title-block">
              <div className="drawer-title">{editing.patient_name}</div>
              <div className="drawer-subtitle">
                Atribuição comercial
                <span className="drawer-subtitle-sep">·</span>
                {editing.proposal_id
                  ? `Deal #${editing.proposal_id}`
                  : `Cirurgia histórica #${editing.legacy_surgery_id}`}
              </div>
            </div>
          }
        >
          <div className="cc-drawer">
            <p className="cc-muted">
              {editing.proposal_id
                ? `A correção vale para todas as cirurgias do deal #${editing.proposal_id}.`
                : 'Cirurgia histórica sem deal vinculado.'}
            </p>
            <AttributionForm
              attribution={editing}
              onSaved={() => setEditing(null)}
              onCancel={() => setEditing(null)}
            />
            {!!editing.history_json?.length && (
              <section className="cc-drawer-section">
                <h3>Histórico de correções</h3>
                <ul className="cc-history-list">
                  {editing.history_json.map((change, i) => {
                    const before = change.before as Record<string, unknown> | undefined
                    const after = change.after as Record<string, unknown> | undefined
                    return (
                      <li key={i}>
                        <span>
                          {shortDate(String(change.at))} · {userName(change.actor_id)}
                        </span>
                        <p>{String(change.reason)}</p>
                        <small>
                          Originador: {userName(before?.originator_user_id)} →{' '}
                          {userName(after?.originator_user_id)}
                          <br />
                          Fechador: {userName(before?.closer_user_id)} →{' '}
                          {userName(after?.closer_user_id)}
                        </small>
                      </li>
                    )
                  })}
                </ul>
              </section>
            )}
          </div>
        </Drawer>
      )}
    </section>
  )
}
